import { apiClient } from './api-client'

export interface UserOab {
  id: string
  userId: string
  numero: string
  uf: string
  monitorar: boolean
  label: string
}

export interface TeamMember {
  id: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  role: string
  officeRole: string | null
  isActive: boolean
  oabs: UserOab[]
}

export interface ApiKeyRecord {
  id: string
  userId: string
  userName: string
  label: string
  prefix: string
  createdAt: string
  lastUsedAt: string | null
  revokedAt: string | null
}

export interface CreatedApiKey {
  key: string
  record: ApiKeyRecord
}

/** Mensagem de erro da API (`{ error: { message } }`) ou do axios. */
export function apiErrorMessage(error: unknown, fallback = 'Não foi possível concluir'): string {
  const data = (error as { response?: { data?: { error?: { message?: unknown }; message?: unknown } } })?.response?.data
  if (typeof data?.error?.message === 'string') return data.error.message
  if (typeof data?.message === 'string') return data.message
  return error instanceof Error ? error.message : fallback
}

/** Equipe do escritório: papéis, OABs e chaves de API do agente. */
class TeamService {
  async listMembers(): Promise<TeamMember[]> {
    const response = await apiClient.get('/api/users')
    return response.data.data
  }

  async changeRole(userId: string, role: string): Promise<void> {
    await apiClient.put(`/api/auth/users/${userId}/role`, { role })
  }

  async addOab(userId: string, input: { numero: string; uf: string; monitorar: boolean }): Promise<UserOab> {
    const response = await apiClient.post(`/api/users/${userId}/oabs`, input)
    return response.data.data
  }

  async setOabMonitorar(oabId: string, monitorar: boolean): Promise<UserOab> {
    const response = await apiClient.patch(`/api/users/oabs/${oabId}`, { monitorar })
    return response.data.data
  }

  async removeOab(oabId: string): Promise<void> {
    await apiClient.delete(`/api/users/oabs/${oabId}`)
  }

  async listApiKeys(): Promise<ApiKeyRecord[]> {
    const response = await apiClient.get('/api/auth/api-keys')
    return response.data.data
  }

  async createApiKey(userId: string, label: string): Promise<CreatedApiKey> {
    const response = await apiClient.post('/api/auth/api-keys', { userId, label })
    return response.data.data
  }

  async revokeApiKey(id: string): Promise<ApiKeyRecord> {
    const response = await apiClient.post(`/api/auth/api-keys/${id}/revoke`)
    return response.data.data
  }
}

export const teamService = new TeamService()
